// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getUpsellsProductsData } from "@guestsupply/App/Services/UpsellsProducts";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler, HasOnError } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

interface Props {
    product: ProductModel;
    productsInfo: ProductInfo[];
    pricingLoaded?: boolean;
    skipPricingAndInventory: boolean;
    pids: string[];
    apiResult: ProductCollectionModel;
    error: string;
}

type HandlerType = Handler<{} & HasOnError, Props>;

export const GetUpsellsProductsFromApi: HandlerType = async props => {
    try {
        props.apiResult = await getUpsellsProductsData();
    } catch (error) {
        props.error = error?.message || translate("An error occurred");
    }

    let prodsInfo = [] as ProductInfo[];
    const pids: any = [];
    if (props.apiResult?.products && props.apiResult?.products?.length > 0) {
        prodsInfo = props.apiResult.products.map((item: any) => {
            pids.push(item.id);
            const productInfo = createFromProduct(item);
            (productInfo as any).erpNumber = item.productNumber;
            return productInfo;
        });
    }
    if (prodsInfo.length > 0) {
        props.productsInfo = prodsInfo;
        props.pids = pids;
    }
};
export const LoadPricing: HandlerType = async props => {
    if (props.skipPricingAndInventory) {
        return;
    }

    let pricingLoaded = false;
    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: props.productsInfo,
            onSuccess: realTimePricing => {
                if (realTimePricing) {
                    props.productsInfo = props.productsInfo.map(item => {
                        const pricing = realTimePricing.realTimePricingResults?.find(
                            o => o.productId === item.productId,
                        );
                        return { ...item, pricing };
                    });
                }
                pricingLoaded = true;
            },
            onError: () => {
                pricingLoaded = true;
                if (props.productsInfo) {
                    props.productsInfo = props.productsInfo.map(item => {
                        return { ...item, failedToLoadPricing: true };
                    });
                }
                SetProductInfo(props);
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        }),
    );
    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => pricingLoaded);
    }
};

export const LoadInventory: HandlerType = async props => {
    if (props.skipPricingAndInventory) {
        return;
    }
    const pids = props.pids;
    if (!pids || pids.length === 0) {
        return;
    }

    let loadedInventory = false;

    props.dispatch(
        loadRealTimeInventory({
            productIds: pids,
            onComplete: realTimeInventoryProps => {
                if (realTimeInventoryProps.error) {
                    if (props.productsInfo) {
                        props.productsInfo &&
                            props.productsInfo.forEach(item => {
                                item.failedToLoadInventory = true;
                            });
                    }
                } else if (props.productsInfo) {
                    props.productsInfo = props.productsInfo.map(item => {
                        const inventory = realTimeInventoryProps.apiResult?.realTimeInventoryResults?.find(
                            o => o.productId === item.productId,
                        );
                        return { ...item, inventory };
                    });
                }
                loadedInventory = true;
            },
        }),
    );

    await waitFor(() => loadedInventory);
};
export const ExecuteOnErrorCallback: HandlerType = props => {
    if (props.error) {
        props.parameter.onError?.(props.error as any);
    }
};

export const SetProductInfo: HandlerType = props => {
    if (props.productsInfo) {
        let productsInfo = {};
        props.productsInfo.forEach(item => {
            const erpNumber = (item as any).erpNumber;
            productsInfo = {
                [erpNumber]: item,
                ...productsInfo,
            };
        });
        props.dispatch({
            type: "UpsellsProducts/AddProductsInfo",
            productsInfo,
        } as any);
    }
};
export const chain = [GetUpsellsProductsFromApi, LoadPricing, LoadInventory, SetProductInfo, ExecuteOnErrorCallback];

const getUpsellsProducts = createHandlerChainRunner(chain, "getUpsellsProducts");
export default getUpsellsProducts;
